/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [index.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 26.09.18 21:01
 */
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'bootstrap/dist/js/bootstrap';

import React, {Fragment, lazy, Suspense} from 'react';
import ReactDOM from 'react-dom';
import {BrowserRouter, Route, Switch} from 'react-router-dom';
import {Provider} from 'react-redux';
import {applyMiddleware, createStore} from 'redux';
import thunk from 'redux-thunk';
import promiseMiddleware from 'redux-promise';

import Navigation from './components/navigation/navigation';
import HomeSpace from './spaces/home-space';
import {FriendsMobileChat} from "./spaces/friends-space";
import SimpleLandingPage from "./components/landingpage/simple-landing-page";
import Configuration from "./components/configuration/configuration";
import Footer from "./components/billboard-footer/footer";
import PageNotFound from "./components/not-found/page-not-found";
import ErrorPage from "./components/not-found/error-page";
import reducers from './reducers';
import {NoInternetConnection, ServerNotAlive} from "./components/not-found/no-internet";
import {isMobileMozilla} from "./components/configuration/mobile";
import {isOnline, isServerAlive} from "./actions/error-handling";
import './index.scss';
// import SlideoutProvider from "./components/slideout-navigation/slideout-provider";


// simple redux logger
const logMiddleware = store => next => action => {
    console.log('>>>', action, store.getState());
    next(action);
};

// uncomment for activating logging
// const middleware = [thunk, promiseMiddleware, logMiddleware];
const middleware = [thunk, promiseMiddleware];
const store = createStore(reducers, applyMiddleware(...middleware));

// code splitting
// using suspense to lazy load not used components and improve on initial app loading
const LoginForm = lazy(() => import('./components/forms/login/login-form'));
const RequestPassword = lazy(() => import('./components/forms/login/request-password'));
const ResetPassword = lazy(() => import('./components/forms/login/reset-password'));
const ConfirmEmail = lazy(() => import('./components/forms/login/confirm-email'));
const CreateAccountForm = lazy(() => import('./components/forms/create-account/create-account-form'));
const UpdateAccount = lazy(() => import('./components/forms/manage-user-account/update-account'));
const ManageSite = lazy(() => import('./components/forms/manage-site/manage-site'));
const ManageSpace = lazy(() => import('./components/forms/manage-spaces/manage-space'));
const ManageSpaces = lazy(() => import('./components/forms/manage-spaces/manage-spaces'));
const ManageUsers = lazy(() => import('./components/forms/users/manage-users'));
const ManagePage = lazy(() => import('./components/forms/page/manage-page'));
const ManagePages = lazy(() => import('./components/forms/page/manage-pages'));
const ManageMenus = lazy(() => import('./components/forms/menu/manage-space-submenus'));
const GallerySpace = lazy(() => import('./spaces/gallery-space'));
const MembersSpace = lazy(() => import('./spaces/members-space'));
const FriendsSpace = lazy(() => import('./spaces/friends-space'));
const CalendarSpace = lazy(() => import('./spaces/calendar-space'));
const GenericSpace = lazy(() => import('./spaces/generic-space'));
const RestrictedAccessSpace = lazy(() => import('./spaces/restricted-access-space'));
const StandardPage = lazy(() => import('./spaces/standard-page'));
const SlideoutProvider = lazy(() => import('./components/slideout-navigation/slideout-provider'));

const DesktopApp = () => {
    return <Fragment>
        <Suspense fallback={<div>Loading...</div>}>
            <Navigation/>
            <Switch>
                <Route exact path={"/login"} component={LoginForm}/>
                <Route exact path={"/request/password"} component={RequestPassword}/>
                <Route exact path={"/reset/password"} component={ResetPassword}/>
                <Route exact path={"/confirm/email"} component={ConfirmEmail}/>
                <Route exact path={"/create/account"} component={CreateAccountForm}/>
                <Route exact path={"/update/account"} component={UpdateAccount}/>
                <Route exact path={"/manage/site"} component={ManageSite}/>
                <Route exact path={"/manage/space"} component={ManageSpace}/>
                <Route exact path={"/manage/spaces"} component={ManageSpaces}/>
                <Route exact path={"/manage/users"} component={ManageUsers}/>
                <Route exact path={"/manage/page"} component={ManagePage}/>
                <Route exact path={"/manage/pages"} component={ManagePages}/>
                <Route exact path={"/:username/manage/menus/:spaceId"} component={ManageMenus}/>
                <Route exact path={"/page-not-found"} component={PageNotFound}/>
                <Route exact path={"/error-page"} component={ErrorPage}/>
                <Route path="/:username" exact strict component={HomeSpace}/>
                <Route path="/:username/home" exact strict component={HomeSpace}/>
                <Route path="/:username/space/:spaceId" exact strict component={GenericSpace}/>
                <Route path="/:username/gallery/:spaceId" exact strict component={GallerySpace}/>
                <Route path="/:username/members/:spaceId" exact strict component={MembersSpace}/>
                <Route path="/:username/friends" exact strict component={FriendsSpace}/>
                <Route path="/:username/friends/mobile" exact strict component={FriendsSpace}/>
                <Route path="/:username/friends/mobile/chat" exact strict component={FriendsMobileChat}/>
                <Route path="/:username/restricted" exact strict component={RestrictedAccessSpace}/>
                <Route path="/:username/calendar" exact strict component={CalendarSpace}/>
                <Route path="/:username/page/:pagename" exact strict component={StandardPage}/>
                <Route path="/:nomatch" component={PageNotFound}/>
                <Route path="/" component={SimpleLandingPage}/>
            </Switch>
            <Footer/>
        </Suspense>
    </Fragment>
}

const MobileApp = () => {
    return <Fragment>
        <Suspense fallback={<div>Loading...</div>}>
            <SlideoutProvider>
                <DesktopApp/>
            </SlideoutProvider>
        </Suspense>
    </Fragment>
}

const renderApp = () => {
    if (isServerAlive()) {
        return isMobileMozilla() ? <MobileApp/> : <DesktopApp/>
    }
    return <ServerNotAlive/>
}

ReactDOM.render(isOnline() ?
    <Provider store={store}>
        <Configuration>
            <BrowserRouter>
                {renderApp()}
            </BrowserRouter>
        </Configuration>
    </Provider>
:
    <NoInternetConnection/>

, document.getElementById('root'), () => {
});
